import React, { useState, useEffect } from 'react';
import { 
  Settings, Database, Mail, Shield, Globe, Bell, Key, Save, 
  Check, X, RefreshCw, Eye, EyeOff, TestTube
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const SystemConfig = () => {
  const [config, setConfig] = useState({
    platform_name: 'CNESS Platform',
    platform_url: '',
    admin_email: '',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_password: '',
    notification_settings: {
      email_notifications: true,
      assessment_reminders: true,
      certification_alerts: true
    },
    security_settings: {
      password_min_length: 8,
      session_timeout_minutes: 60,
      max_login_attempts: 5
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('platform');
  const [showPassword, setShowPassword] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/system-config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/system-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Configuration saved successfully!' });
      } else {
        setSaveMessage({ type: 'error', text: 'Failed to save configuration' });
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setSaveMessage({ type: 'error', text: 'Error saving configuration' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/system-config/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smtp_host: config.smtp_host,
          smtp_port: config.smtp_port,
          smtp_user: config.smtp_user
        })
      });
      
      if (response.ok) {
        alert('Email configuration is valid!');
      } else {
        const data = await response.json();
        alert(`Email test failed: ${data.detail}`);
      }
    } catch (error) {
      alert('Error testing email configuration');
    }
  };

  const sections = [
    { id: 'platform', label: 'Platform Settings', icon: Globe, color: 'blue' },
    { id: 'email', label: 'Email Configuration', icon: Mail, color: 'green' },
    { id: 'security', label: 'Security Settings', icon: Shield, color: 'red' },
    { id: 'notifications', label: 'Notifications', icon: Bell, color: 'yellow' },
    { id: 'database', label: 'Database Info', icon: Database, color: 'purple' }
  ];

  if (loading) {
    return (
      <SystemAdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading configuration...</p>
          </div>
        </div>
      </SystemAdminLayout>
    );
  }

  return (
    <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Settings className="h-10 w-10" />
                <div>
                  <h1 className="text-4xl font-black mb-2">System Configuration</h1>
                  <p className="text-lg text-indigo-100">Manage platform settings and configurations</p>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-white text-indigo-600 px-6 py-3 rounded-lg font-bold hover:bg-indigo-50 transition-all disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`max-w-7xl mx-auto px-8 py-4`}>
            <div className={`flex items-center space-x-2 p-4 rounded-lg ${
              saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {saveMessage.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
              <span className="font-semibold">{saveMessage.text}</span>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex gap-8">
            {/* Sidebar Navigation */}
            <div className="w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg p-4 sticky top-8">
                <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">Configuration Sections</h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                          activeSection === section.id
                            ? `bg-${section.color}-100 text-${section.color}-700 font-semibold`
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {/* Platform Settings */}
              {activeSection === 'platform' && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Platform Settings</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Platform Name</label>
                      <input
                        type="text"
                        value={config.platform_name}
                        onChange={(e) => setConfig({ ...config, platform_name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Platform URL</label>
                      <input
                        type="url"
                        value={config.platform_url || window.location.origin}
                        onChange={(e) => setConfig({ ...config, platform_url: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Email</label>
                      <input
                        type="email"
                        value={config.admin_email}
                        onChange={(e) => setConfig({ ...config, admin_email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Email Configuration */}
              {activeSection === 'email' && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-6 h-6 text-green-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">Email Configuration</h2>
                    </div>
                    <button
                      onClick={handleTestEmail}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all"
                    >
                      <TestTube className="w-4 h-4" />
                      <span>Test Connection</span>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">SMTP Host</label>
                      <input
                        type="text"
                        value={config.smtp_host}
                        onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                        placeholder="smtp.example.com"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">SMTP Port</label>
                      <input
                        type="text"
                        value={config.smtp_port}
                        onChange={(e) => setConfig({ ...config, smtp_port: e.target.value })}
                        placeholder="587"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">SMTP Username</label>
                      <input
                        type="text"
                        value={config.smtp_user}
                        onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">SMTP Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={config.smtp_password}
                          onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeSection === 'security' && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Minimum Password Length
                      </label>
                      <input
                        type="number"
                        min="6"
                        max="20"
                        value={config.security_settings?.password_min_length || 8}
                        onChange={(e) => setConfig({
                          ...config,
                          security_settings: { ...config.security_settings, password_min_length: parseInt(e.target.value) }
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Session Timeout (minutes)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="1440"
                        value={config.security_settings?.session_timeout_minutes || 60}
                        onChange={(e) => setConfig({
                          ...config,
                          security_settings: { ...config.security_settings, session_timeout_minutes: parseInt(e.target.value) }
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Max Login Attempts
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="10"
                        value={config.security_settings?.max_login_attempts || 5}
                        onChange={(e) => setConfig({
                          ...config,
                          security_settings: { ...config.security_settings, max_login_attempts: parseInt(e.target.value) }
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {activeSection === 'notifications' && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Bell className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      { key: 'email_notifications', label: 'Email Notifications', desc: 'Send email notifications for important events' },
                      { key: 'assessment_reminders', label: 'Assessment Reminders', desc: 'Send reminders for pending assessments' },
                      { key: 'certification_alerts', label: 'Certification Alerts', desc: 'Alert when organizations reach certification milestones' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-yellow-300 transition-all">
                        <div>
                          <h4 className="font-semibold text-gray-900">{item.label}</h4>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.notification_settings?.[item.key] ?? true}
                            onChange={(e) => setConfig({
                              ...config,
                              notification_settings: { ...config.notification_settings, [item.key]: e.target.checked }
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Database Info */}
              {activeSection === 'database' && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Database className="w-6 h-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Database Information</h2>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Database Type</p>
                      <p className="text-lg font-semibold text-gray-900">MongoDB Atlas</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Database Name</p>
                      <p className="text-lg font-semibold text-gray-900">cness_db</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Connection Status</p>
                      <p className="text-lg font-semibold text-green-600 flex items-center">
                        <Check className="w-5 h-5 mr-1" /> Connected
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Last Backup</p>
                      <p className="text-lg font-semibold text-gray-900">Automated (Atlas)</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-800">
                      <strong>Note:</strong> Database connection is managed by MongoDB Atlas. 
                      For connection string changes, update the environment variables.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SystemAdminLayout>
  );
};

export default SystemConfig;
